import type { DeliveryOverride, DoorPosition, SubstitutionChoice } from '@croissants/contracts';
import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { CheckoutContactDraft } from '../lib/checkout-validation';
import { readJson, writeJson } from '../lib/storage';

/**
 * Everything the guest has chosen but not yet paid for.
 *
 * It is one object rather than state scattered across screens because of GST-026:
 * "the guest can change item quantities or remove a line without losing the rest
 * of the checkout state". Going back from the review step to the basket and
 * forward again has to be free, and the only way to make it free is for the
 * screens to be views over one persisted draft.
 *
 * The delivery fields here are an order-scoped override (PRD GST-023): they
 * travel with this order and never modify the conciergerie's property record.
 */

const STORAGE_PREFIX = 'cib.draft.';

/**
 * The eight boxes of the apartment's delivery brief, as the guest may correct
 * them for ONE order (GST-023).
 *
 * Eight, and the same eight the owner fills in on their own screen, in the same
 * order and under the same words (founder decision 2026-08-24). The two typed
 * boxes carry the form's vocabulary rather than the wire's: a `<select>` holds
 * `''` for "leave it as the apartment says", which is not `null` and not
 * `false` - `overrideForWire` is the one place that conversion happens.
 */
export interface DeliveryOverrideDraft {
  readonly building: string;
  readonly floor: string;
  readonly doorPosition: '' | DoorPosition;
  readonly doorNumber: string;
  readonly intercomName: string;
  readonly accessCode: string;
  readonly lift: '' | 'yes' | 'no';
  readonly courierInstructions: string;
  /**
   * GST-007: the guest's leave-at-door switch. `null` is a switch nobody has
   * touched, which is what lets the apartment's own default show through and
   * reach dispatch unchanged - the same "empty means unchanged" rule the text
   * boxes above follow.
   */
  readonly leaveAtDoor: boolean | null;
}

export interface CheckoutDraft {
  /** The ANCHOR morning: the first selected day; the basket's own morning. */
  readonly serviceDay: string | null;
  /** ISO instant of the chosen window's start ON THE ANCHOR DAY (PRD GST-002). */
  readonly windowStart: string | null;
  /**
   * PRD RTE-034: the guest tapped today's ASAP row - "as soon as we can" -
   * rather than naming a time on it.
   *
   * Carried rather than re-derived from `windowStart`, because the two cannot
   * be told apart by that instant: the ASAP row's window is provisional and its
   * start lands on the offer cadence often enough to matter. This is the only
   * place that knows which row was actually tapped, so it is the place that
   * remembers.
   */
  readonly soonest: boolean;
  /**
   * Every selected morning of the stay, ascending, the anchor included (D11:
   * ONE picker for every order). One entry is a one-day order; several are a
   * stay - the strip is the only producer, and the checkout wire's `repeat`
   * is derived from it (`stayForWire`), never typed at checkout any more.
   */
  readonly selectedDays: readonly string[];
  readonly contact: CheckoutContactDraft;
  readonly substitutionChoice: SubstitutionChoice;
  readonly override: DeliveryOverrideDraft;
  /**
   * NTF-009: "order updates on WhatsApp", ticked. Optional so a draft saved
   * before it existed still reads; absent is no. Order-scoped like the other
   * delivery choices, so `reset` clears it: consent is given per order, never
   * carried silently into the next one.
   */
  readonly whatsappUpdates?: boolean;
  /**
   * GST-163: the guest's note to the house, typed on the basket and sent at
   * checkout. Optional so an older saved draft still reads; order-scoped, so
   * `reset` clears it with the rest of the order's choices.
   */
  readonly guestNote?: string;
}

export const EMPTY_OVERRIDE: DeliveryOverrideDraft = {
  building: '',
  floor: '',
  doorPosition: '',
  doorNumber: '',
  intercomName: '',
  accessCode: '',
  lift: '',
  courierInstructions: '',
  leaveAtDoor: null,
};

export const EMPTY_DRAFT: CheckoutDraft = {
  serviceDay: null,
  windowStart: null,
  soonest: false,
  selectedDays: [],
  contact: { name: '', phone: '' },
  // PRD GST-019: replacing with an equivalent item is the default.
  substitutionChoice: 'REPLACE_EQUIVALENT',
  override: EMPTY_OVERRIDE,
};

function isDraft(value: unknown): value is CheckoutDraft {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<CheckoutDraft>;
  return typeof candidate.contact === 'object' && candidate.contact !== null;
}

/**
 * The same draft, with any morning that has already gone taken out of it.
 *
 * The draft is persisted per QR code, and the code is screwed to the wall of a
 * flat the guest sleeps in for a week - so a draft routinely outlives the morning
 * it was made for. Carrying Sunday's `serviceDay` into Monday makes every screen
 * ask the server for a catalogue, a window list and a price for a morning that
 * cannot be ordered, and the server answers, correctly but uselessly, that the
 * address is not servable (PRD GST-002, GST-040). The guest reads that as "we do
 * not deliver here" and has no way to correct it, because nothing on the screen
 * mentions a date.
 *
 * `earliestServiceDay` is the next morning that can still be ordered - the
 * session's own cutoff (GST-002). Service days are ISO calendar dates, so a
 * lexical comparison is a chronological one. A later morning is left alone: the
 * guest may legitimately have chosen one further out. A stay whose last morning is
 * already behind us drops those mornings from the selection, for the same reason.
 *
 * It is also the one place a stored draft is normalized: a draft written before
 * the multi-select strip (D11) has no `selectedDays` at all, and one read back
 * without it would make the strip believe nothing was ever chosen. The anchor
 * morning stands in for the day set such a draft did know.
 *
 * Pure, and identity-stable when there is nothing to drop, so it can be applied on
 * every render rather than in an effect that would let one stale request out
 * first.
 */
export function withoutPastMorning(
  draft: CheckoutDraft,
  earliestServiceDay: string | null,
): CheckoutDraft {
  const normalized: CheckoutDraft = Array.isArray(draft.selectedDays)
    ? draft
    : { ...draft, selectedDays: draft.serviceDay === null ? [] : [draft.serviceDay] };
  if (earliestServiceDay === null) return normalized;
  const morningGone = normalized.serviceDay !== null && normalized.serviceDay < earliestServiceDay;
  const staleDays = normalized.selectedDays.some((day) => day < earliestServiceDay);
  if (!morningGone && !staleDays) return normalized;
  const selectedDays = normalized.selectedDays.filter((day) => day >= earliestServiceDay);
  return {
    ...normalized,
    selectedDays,
    // The anchor is the FIRST selected morning; when yesterday's anchor goes,
    // its window instant goes with it and the picker re-seeds both.
    ...(morningGone ? { serviceDay: null, windowStart: null, soonest: false } : {}),
  };
}

export interface CheckoutContextValue {
  readonly draft: CheckoutDraft;
  readonly update: (patch: Partial<CheckoutDraft>) => void;
  readonly updateContact: (patch: Partial<CheckoutContactDraft>) => void;
  readonly updateOverride: (patch: Partial<DeliveryOverrideDraft>) => void;
  readonly reset: () => void;
}

const CheckoutContext = createContext<CheckoutContextValue | null>(null);

export function CheckoutProvider({
  children,
  scope,
  earliestServiceDay = null,
}: {
  children: ReactNode;
  scope: string | null;
  /**
   * The next morning that can still be ordered, from the session's cutoff
   * (PRD GST-002). Null until the session has resolved, which is why the rule is
   * applied on read rather than once on restore.
   */
  earliestServiceDay?: string | null;
}): JSX.Element {
  const storageKey = `${STORAGE_PREFIX}${scope ?? 'anonymous'}`;
  const [stored, setStored] = useState<CheckoutDraft>(
    () => readJson<CheckoutDraft>(storageKey, isDraft) ?? EMPTY_DRAFT,
  );

  const fresh = useCallback(
    (candidate: CheckoutDraft) => withoutPastMorning(candidate, earliestServiceDay),
    [earliestServiceDay],
  );
  /**
   * Derived on render, not repaired in an effect. Effects run child-first, so a
   * screen mounted in the same commit would already have asked the server about
   * yesterday before this component got its turn - and that request is the one
   * that comes back "not servable".
   */
  const draft = useMemo(() => fresh(stored), [fresh, stored]);

  useEffect(() => {
    writeJson(storageKey, draft);
  }, [storageKey, draft]);

  const value = useMemo<CheckoutContextValue>(
    () => ({
      draft,
      update: (patch) => setStored((current) => ({ ...fresh(current), ...patch })),
      updateContact: (patch) =>
        setStored((current) => {
          const base = fresh(current);
          return { ...base, contact: { ...base.contact, ...patch } };
        }),
      updateOverride: (patch) =>
        setStored((current) => {
          const base = fresh(current);
          return { ...base, override: { ...base.override, ...patch } };
        }),
      // The window and the delivery choices are order-scoped; the contact details
      // are not, and re-typing a phone number for a second morning is exactly the
      // friction the three-minute journey cannot afford.
      reset: () =>
        setStored((current) => ({
          ...EMPTY_DRAFT,
          contact: current.contact,
        })),
    }),
    [draft, fresh],
  );

  return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>;
}

export function useCheckoutDraft(): CheckoutContextValue {
  const value = useContext(CheckoutContext);
  if (value === null) throw new Error('useCheckoutDraft must be used inside <CheckoutProvider>');
  return value;
}

/**
 * True when this draft carries anything that will reach the courier in place of
 * the property's own record (PRD GST-021, GST-023).
 *
 * The review screen uses it to decide whether the corrections section may open
 * closed: an override the guest never opened the section to type is exactly the
 * thing GST-021 says may not be hidden from this screen.
 */
export function hasDeliveryOverride(draft: DeliveryOverrideDraft): boolean {
  return overrideForWire(draft) !== null;
}

/**
 * The wire shape of the order-scoped override, or null when the guest changed
 * nothing. Empty strings become nulls: an empty field means "unchanged", not
 * "clear the value the host recorded" (PRD GST-023).
 */
export function overrideForWire(draft: DeliveryOverrideDraft): DeliveryOverride | null {
  const value = (raw: string): string | null => (raw.trim() === '' ? null : raw.trim());
  const wire: DeliveryOverride = {
    building: value(draft.building),
    floor: value(draft.floor),
    doorPosition: draft.doorPosition === '' ? null : draft.doorPosition,
    doorNumber: value(draft.doorNumber),
    intercomName: value(draft.intercomName),
    accessCode: value(draft.accessCode),
    lift: draft.lift === '' ? null : draft.lift === 'yes',
    /*
     * The one field on this shape nothing collects any more (founder decision
     * 2026-08-24). The courier is given the name on the payment, which a wallet
     * or a card already knows (GST-006), so the sheet stopped asking - and the
     * field stays on the wire only because orders placed before that carry one.
     */
    recipientName: null,
    courierInstructions: value(draft.courierInstructions),
    leaveAtDoor: draft.leaveAtDoor,
  };
  /*
   * `lift: false` is a guest SAYING there is no lift, which is a correction
   * worth sending - so emptiness is tested against null rather than against
   * falsiness, and `recipientName` is excluded because it is never anything
   * else.
   */
  const { recipientName: _ignored, ...answered } = wire;
  const touched = Object.values(answered).some((entry) => entry !== null);
  return touched ? wire : null;
}
